/**
 * Escalation list state machine - replaces useEscalationStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type { EscalationEvent, EscalationPolicy } from '../types'

export interface EscalationListContext {
  events: EscalationEvent[]
  policies: EscalationPolicy[]
}

export type EscalationListEvent =
  | { type: 'ADD_EVENT'; event: EscalationEvent }
  | { type: 'UPDATE_EVENT'; id: string; updates: Partial<EscalationEvent> }
  | { type: 'SET_POLICIES'; policies: EscalationPolicy[] }
  | { type: 'ADD_POLICY'; policy: EscalationPolicy }
  | { type: 'UPDATE_POLICY'; id: string; updates: Partial<EscalationPolicy> }
  | { type: 'DELETE_POLICY'; id: string }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as EscalationListContext,
    events: {} as EscalationListEvent,
  },
})

export const escalationListMachine = machineSetup.createMachine({
  id: 'escalationList',
  context: {
    events: [],
    policies: [],
  },
  on: {
    ADD_EVENT: {
      actions: assign({
        events: ({ context, event }) =>
          event.type === 'ADD_EVENT' ? [...context.events, event.event] : context.events,
      }),
    },
    UPDATE_EVENT: {
      actions: assign({
        events: ({ context, event }) => {
          if (event.type !== 'UPDATE_EVENT') return context.events
          return context.events.map((e) => (e.id === event.id ? { ...e, ...event.updates } : e))
        },
      }),
    },
    SET_POLICIES: {
      actions: assign({
        policies: ({ event }) => (event.type === 'SET_POLICIES' ? event.policies : []),
      }),
    },
    ADD_POLICY: {
      actions: assign({
        policies: ({ context, event }) =>
          event.type === 'ADD_POLICY' ? [...context.policies, event.policy] : context.policies,
      }),
    },
    UPDATE_POLICY: {
      actions: assign({
        policies: ({ context, event }) => {
          if (event.type !== 'UPDATE_POLICY') return context.policies
          return context.policies.map((p) =>
            p.id === event.id ? { ...p, ...event.updates, updatedAt: Date.now() } : p
          )
        },
      }),
    },
    DELETE_POLICY: {
      actions: assign({
        policies: ({ context, event }) => {
          if (event.type !== 'DELETE_POLICY') return context.policies
          return context.policies.filter((p) => p.id !== event.id)
        },
      }),
    },
    CLEAR: {
      actions: assign({ events: [], policies: [] }),
    },
  },
})
